import { Fragment, useRef, useEffect, useState, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import { valueFormat } from "src/helper/valueFormat";
import { itemCheckDiscountLabel } from "src/helper/checkOrderSummary";
import { buildRollupLine, formatModifierOption } from "src/helper/lineTotal";
import { buildPromotionAdjustmentsByLineUuid } from "src/helper/promotionItemAttribution";
import { itemPromotionTitle } from "src/helper/itemPromotions";
import {
  NamePriceRowWrapper,
  DiscountRowWrapper,
  DiscountRowTextWrapper,
  DiscountRowLabelWrapper,
  DiscountRowPriceWrapper,
  MofiferNameWrapper,
  MofiferOptionsNameWrapper,
  MofiferOptionsTextWrapper,
  MofiferOptionsPriceText,
  MofiferOptionsText,
} from "./styles";

const styles = {
  row: {
    marginBottom: 0,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  countCell: {
    textAlign: "left",
    fontSize: "16px",
  },
  nameCell: {
    textAlign: "left",
    padding: "0 16px 0 12px",
    fontSize: "16px",
    fontWeight: 600,
  },
  priceCell: {
    textAlign: "right",
  },
};

const OrderProduct = ({
  product,
  charges,
  orderItems,
  promotionLines = [],
}) => {
  const containerRef = useRef(null);
  const priceRef = useRef(null);
  const modPriceRef = useRef(null);
  const [nameWidth, setNameWidth] = useState("100%");
  const [modPriceWidth, setModPriceWidth] = useState("100%");
  const line = buildRollupLine(product);
  const shownPrice = line.useLineTotal
    ? line.lineTotal
    : Number(product?.price || 0);
  const itemDiscount = useMemo(
    () =>
      charges?.discountDetails?.find(
        (item) => item?.appliesTo === "item" && item?.itemUuid === product?.uuid,
      ),
    [charges?.discountDetails, product?.uuid],
  );
  const promotionAdjustments = useMemo(() => {
    if (!product?.uuid) return [];
    return (
      buildPromotionAdjustmentsByLineUuid(
        orderItems,
        charges?.promotionDetails,
      ).get(product.uuid) || []
    );
  }, [orderItems, charges?.promotionDetails, product?.uuid]);
  const discountPrice =
    itemDiscount?.discountAmount &&
    valueFormat(
      `$${Number(shownPrice - Math.abs(itemDiscount.discountAmount || 0)).toFixed(2)}`,
    );
  const extraPromotionLines = useMemo(() => {
    const shownPromotionLabels = new Set(
      promotionAdjustments.map((adjustment) =>
        String(adjustment.displayLabel || adjustment.promotionName || "")
          .trim()
          .toLowerCase(),
      ),
    );
    const hasItemDiscount = Number(itemDiscount?.discountAmount) > 0;

    return (promotionLines || []).filter((promoLine) => {
      if (promoLine.isCoupon && hasItemDiscount) {
        return false;
      }
      const name = String(promoLine.name || "").trim().toLowerCase();
      if (!promoLine.isCoupon && name && shownPromotionLabels.has(name)) {
        return false;
      }
      return Number(promoLine.amount) > 0;
    });
  }, [itemDiscount, promotionAdjustments, promotionLines]);

  useEffect(() => {
    if (!containerRef.current || !priceRef.current) return;

    const update = () => {
      const container = containerRef.current;
      const price = priceRef.current;
      const containerWidth = container?.offsetWidth;
      const priceWidth = price?.offsetWidth;
      const available = containerWidth - priceWidth; // include safe padding
      const nextModPriceWidth = modPriceRef?.current?.offsetWidth;
      setNameWidth(`${available}px`);
      setModPriceWidth(`${nextModPriceWidth - 32}px`);
    };

    update(); // run once initially

    const resizeObserver = new ResizeObserver(() => {
      update(); // update anytime size changes
    });

    resizeObserver.observe(containerRef.current);
    resizeObserver.observe(priceRef.current);

    return () => resizeObserver.disconnect();
  }, [containerRef?.current, priceRef?.current, modPriceRef?.current]);

  return (
    <>
      <Box sx={styles.row} ref={containerRef}>
        <NamePriceRowWrapper sx={{ maxWidth: nameWidth }}>
          <Typography
            sx={{ ...styles.countCell, width: 36, flexShrink: 0 }}
          >{`${product.quantity || product.count || 1}×`}</Typography>

          <Typography
            className="uppercase"
            sx={{
              ...styles.nameCell,
              maxWidth: nameWidth,
            }}
          >
            {product?.name}
          </Typography>
        </NamePriceRowWrapper>

        <Box ref={priceRef}>
          <Typography
            sx={{
              width: priceRef?.current?.offsetWidth,
            }}
          >
            {valueFormat(`$${Number(shownPrice).toFixed(2)}`)}
          </Typography>
        </Box>
      </Box>
      {line.description ? (
        <Typography
          sx={{ fontSize: "13px", fontWeight: 500, paddingLeft: "44px" }}
        >
          {line.description}
        </Typography>
      ) : null}

      {(product?.modifier || product?.modifierGroup)?.length > 0 &&
        (product?.modifier || product?.modifierGroup).map((mod) => (
          <Fragment key={mod?._id || mod?.pathKey}>
            {mod?.hideNameOnOrder === true ? null : (
              <MofiferNameWrapper
                sx={{
                  marginRight: `${
                    priceRef?.current?.offsetWidth || 0
                  }px !important`,
                }}
                className="modifier-main-name"
              >
                {(mod?.name?.en || mod?.name || "").toUpperCase()}
              </MofiferNameWrapper>
            )}

            {(mod?.list || mod?.modifierOptions || []).map((item) => {
              const option = formatModifierOption(item, mod);
              if (!option) return null;
              return (
                <Fragment key={item?._id}>
                  <MofiferOptionsNameWrapper>
                    <MofiferOptionsTextWrapper sx={{ maxWidth: nameWidth }}>
                      <MofiferOptionsText>
                        {option.showQty ? <b>{`${option.qtyLabel} `}</b> : null}
                        <span>{option.name}</span>
                      </MofiferOptionsText>
                    </MofiferOptionsTextWrapper>

                    {option.priceLabel ? (
                      <MofiferOptionsPriceText ref={modPriceRef}>
                        {option.priceLabel}
                      </MofiferOptionsPriceText>
                    ) : null}
                  </MofiferOptionsNameWrapper>
                  {option.explanation ? (
                    <Typography sx={{ fontSize: "13px", paddingLeft: "44px" }}>
                      {option.explanation}
                    </Typography>
                  ) : null}
                </Fragment>
              );
            })}
          </Fragment>
        ))}
      {itemDiscount?.discountAmount > 0 ? (
        <DiscountRowWrapper>
          <DiscountRowTextWrapper>
            <DiscountRowLabelWrapper>
              {itemCheckDiscountLabel(itemDiscount)}
            </DiscountRowLabelWrapper>
          </DiscountRowTextWrapper>
          <DiscountRowPriceWrapper>{discountPrice}</DiscountRowPriceWrapper>
        </DiscountRowWrapper>
      ) : null}
      {promotionAdjustments.map((adjustment) => (
        <DiscountRowWrapper
          key={`${adjustment.displayLabel}-${adjustment.discountAmount}`}
        >
          <DiscountRowTextWrapper>
            <DiscountRowLabelWrapper>
              {`Promotion (${adjustment.displayLabel}) - Item`}
            </DiscountRowLabelWrapper>
          </DiscountRowTextWrapper>
          <DiscountRowPriceWrapper>
            {valueFormat(
              `($${Number(adjustment.discountAmount || 0).toFixed(2)})`,
            )}
          </DiscountRowPriceWrapper>
        </DiscountRowWrapper>
      ))}
      {extraPromotionLines.map((promoLine) => (
        <Box key={promoLine.key} className="item-promotion">
          <span className="item-promotion-label">
            {itemPromotionTitle(
              promoLine.isCoupon ? "Coupon" : "Promotion",
              promoLine.name,
            )}
          </span>
          <span className="item-promotion-price">
            {valueFormat(`($${Number(promoLine.amount || 0).toFixed(2)})`)}
          </span>
        </Box>
      ))}
    </>
  );
};
export default OrderProduct;
